import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Send,
  MoreVert,
  Edit,
  Delete,
  ThumbUp,
  ThumbUpOutlined
} from '@mui/icons-material';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import VideoApiService from '../../services/video-api-service';
import { UserContext } from '../../context/userContext';

const VideoComments = ({ videoId, commentCount }) => {
  const { userData } = React.useContext(UserContext);
  
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  
  // Helper function to check if current user owns the comment
  const isCommentOwner = (comment) => {
    if (!userData || !userData._id || !comment.user || !comment.user._id) {
      console.log('isCommentOwner check failed - missing data:', {
        hasUserData: !!userData,
        userDataId: userData?._id,
        hasCommentUser: !!comment.user,
        commentUserId: comment.user?._id
      });
      return false;
    }
    // Compare both as strings to handle potential type mismatches
    const isOwner = String(userData._id) === String(comment.user._id);
    console.log('isCommentOwner check:', {
      userDataId: userData._id,
      commentUserId: comment.user._id,
      isOwner
    });
    return isOwner;
  };

  useEffect(() => {
    console.log('VideoComments - userData:', userData);
    console.log('VideoComments - userData._id:', userData?._id);
    console.log('VideoComments - userData type:', typeof userData);
    if (userData) {
      console.log('VideoComments - userData keys:', Object.keys(userData));
    }
    fetchComments();
  }, [videoId]);

  const fetchComments = async (pageNum = 1) => {
    try {
      setLoading(true);
      console.log('Fetching comments for video:', videoId, 'page:', pageNum);
      const response = await VideoApiService.getComments(videoId, pageNum, 20);
      console.log('Raw comments response:', response);
      
      if (response && response.ok) {
        const { comments, pagination } = response.result;
        console.log('Fetched comments:', comments);
        console.log('Current user:', userData);
        console.log('Current user ID:', userData?._id);
        
        // Log detailed user data for debugging
        comments.forEach((comment, index) => {
          console.log(`Comment ${index} user data:`, {
            commentId: comment._id,
            user: comment.user,
            userId: comment.user?._id,
            firstName: comment.user?.firstName,
            lastName: comment.user?.lastName,
            profileImg: comment.user?.profileImg,
            hasUser: !!comment.user,
            userKeys: comment.user ? Object.keys(comment.user) : []
          });
        });
        
        if (pageNum === 1) {
          setComments(comments);
        } else {
          setComments(prev => [...prev, ...comments]);
        }
        setHasMore(pagination.hasNextPage);
        setPage(pageNum);
      } else {
        console.error('Comments response not ok:', response);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      console.error('Error details:', error.response || error.message || error);
      console.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setSubmitting(true);
      const response = await VideoApiService.postComment(videoId, newComment.trim());
      
      if (response.ok) {
        console.log('New comment full response:', response);
        console.log('New comment result:', response.result);
        console.log('New comment user:', response.result.user);
        console.log('New comment user details:', {
          hasUser: !!response.result.user,
          userId: response.result.user?._id,
          firstName: response.result.user?.firstName,
          lastName: response.result.user?.lastName,
          userKeys: response.result.user ? Object.keys(response.result.user) : []
        });
        
        // If the new comment doesn't have user data populated, add it from context
        const newCommentWithUser = {
          ...response.result,
          user: response.result.user || {
            _id: userData._id,
            firstName: userData.firstName,
            lastName: userData.lastName,
            profileImg: userData.profileImg
          }
        };
        
        setComments(prev => [newCommentWithUser, ...prev]);
        setNewComment('');
        console.log('Comment posted successfully');
      }
    } catch (error) {
      console.error('Error posting comment:', error);
      console.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editText.trim()) return;
    
    try {
      const response = await VideoApiService.editComment(commentId, editText.trim());
      
      if (response.ok) {
        setComments(prev => prev.map(comment => 
          comment._id === commentId 
            ? { ...comment, content: editText.trim(), isEdited: true, editedAt: new Date() }
            : comment
        ));
        setEditingComment(null);
        setEditText('');
        console.log('Comment updated successfully');
      }
    } catch (error) {
      console.error('Error editing comment:', error);
      console.error('Failed to edit comment');
    }
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;
    
    try {
      const response = await VideoApiService.deleteComment(commentToDelete._id);
      
      if (response.ok) {
        setComments(prev => prev.filter(comment => comment._id !== commentToDelete._id));
        setDeleteDialogOpen(false);
        setCommentToDelete(null);
        console.log('Comment deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      console.error('Failed to delete comment');
    }
  };

  const handleLikeComment = async (commentId, isLiked) => {
    try {
      const response = isLiked 
        ? await VideoApiService.unlikeComment(commentId)
        : await VideoApiService.likeComment(commentId);
      
      if (response.ok) {
        setComments(prev => prev.map(comment => 
          comment._id === commentId 
            ? { 
                ...comment, 
                likesCount: isLiked ? comment.likesCount - 1 : comment.likesCount + 1,
                isLiked: !isLiked 
              }
            : comment
        ));
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleMenuOpen = (event, comment) => {
    setAnchorEl(event.currentTarget);
    setSelectedComment(comment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedComment(null);
  };

  const handleStartEdit = () => {
    setEditingComment(selectedComment._id);
    setEditText(selectedComment.content);
    handleMenuClose();
  };

  const handleStartDelete = () => {
    setCommentToDelete(selectedComment);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {commentCount} Comments
      </Typography>

      {/* Add Comment */}
      {userData ? (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Avatar src={userData?.profileImg?.filePath} />
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmitComment();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#1a1a1a',
                  color: '#fff'
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                variant="text"
                onClick={() => setNewComment('')}
                disabled={!newComment || submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                endIcon={<Send />}
                onClick={handleSubmitComment}
                disabled={!newComment.trim() || submitting}
                sx={{ ml: 1 }}
              >
                Comment
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please sign in to comment
        </Alert>
      )}

      {/* Comments List */}
      {loading && comments.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : comments.length === 0 ? (
        <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
          No comments yet. Be the first to comment!
        </Typography>
      ) : (
        <>
          {comments.map((comment) => (
            <Box key={comment._id} sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Avatar 
                src={comment.user?.profileImg?.filePath} 
                sx={{ cursor: 'pointer' }}
                onClick={() => comment.user?._id && (window.location.href = `/artist/${comment.user._id}`)}
              />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => comment.user?._id && (window.location.href = `/artist/${comment.user._id}`)}
                  >
                    {(() => {
                      console.log('Rendering name for comment:', {
                        commentId: comment._id,
                        user: comment.user,
                        firstName: comment.user?.firstName,
                        lastName: comment.user?.lastName
                      });
                      if (!comment.user) return 'Anonymous';
                      const firstName = comment.user.firstName || '';
                      const lastName = comment.user.lastName || '';
                      const fullName = `${firstName} ${lastName}`.trim();
                      console.log('Computed name:', fullName || 'Anonymous');
                      return fullName || 'Anonymous';
                    })()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                    {comment.isEdited && ' (edited)'}
                  </Typography>
                </Box>

                {editingComment === comment._id ? (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      sx={{
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#1a1a1a',
                          color: '#fff'
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setEditingComment(null);
                          setEditText('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleEditComment(comment._id)}
                        disabled={!editText.trim()}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {comment.content}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleLikeComment(comment._id, comment.isLiked)}
                      >
                        {comment.isLiked ? <ThumbUp fontSize="small" /> : <ThumbUpOutlined fontSize="small" />}
                      </IconButton>
                      <Typography variant="caption">
                        {comment.likesCount || 0}
                      </Typography>
                      
                      {isCommentOwner(comment) && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, comment)}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          ))}

          {/* Load More */}
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => fetchComments(page + 1)}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load More Comments'}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleStartEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleStartDelete}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Comment</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this comment? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteComment} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VideoComments;